// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import { Loader2, Syringe, ShieldAlert, Droplets, Star } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import type { ProtocolSlot } from '../../api/carerxProtocols'
import { useProtocolLibrary } from '../../hooks/queries/carerxProtocols'
import AppButton from '../../components/ui/AppButton'
import ProtocolEditorDialog from '../pharmacy/drugref/ProtocolEditorDialog'
import ProtocolRetireDialog from '../pharmacy/drugref/ProtocolRetireDialog'

/**
 * D-CRX-46 LB-2 — administration protocol library. Lists the Flushing / Premedication /
 * Anaphylaxis-kit protocols by category with their component lines, default per category, and the
 * enrollment-form section each prints to. D-CRX-89: create / edit / retire here too, with the SAME
 * dialogs the drug record mounts (L-067) — a new protocol made here is assigned to no drug; Drug
 * Reference picks which protocol each drug uses. The tab is admin-only, as are the server writes.
 */
const CATEGORIES: { key: string; slot: ProtocolSlot; label: string; prints: string; icon: ReactNode }[] = [
  { key: 'flushing', slot: 'flush', label: 'Flushing', prints: '§06', icon: <Droplets size={15} /> },
  { key: 'premedication', slot: 'premed', label: 'Premedication', prints: '§05', icon: <Syringe size={15} /> },
  { key: 'anaphylaxis_kit', slot: 'kit', label: 'Anaphylaxis kit', prints: '§07', icon: <ShieldAlert size={15} /> },
]

export default function CareRxAdminProtocols() {
  const library = useProtocolLibrary()
  const protocols = useMemo(() => library.data?.protocols ?? [], [library.data])
  const components = useMemo(() => library.data?.components ?? [], [library.data])
  const [editor, setEditor] = useState<{ slot: ProtocolSlot; protocol: api.AdminProtocol | null } | null>(null)
  const [retiring, setRetiring] = useState<api.AdminProtocol | null>(null)

  const byCat = useMemo(() => {
    const m: Record<string, api.AdminProtocol[]> = {}
    for (const p of protocols) (m[p.category] ??= []).push(p)
    return m
  }, [protocols])

  const compByType = useMemo(() => {
    const m: Record<string, api.AdminProtocolComponent[]> = {}
    for (const c of components) (m[c.comp_type] ??= []).push(c)
    return m
  }, [components])

  if (library.isLoading) return <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: muted.color, padding: 20 }}><Loader2 size={16} className="spin" /> Loading protocol library…</div>
  if (library.isError) return <div style={{ fontSize: 13, color: '#b91c1c', padding: 12 }}>{friendlyError(library.error)}</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <div style={cardTitle}>Administration protocol library</div>
        <p style={{ ...muted, marginTop: 4 }}>
          Component-catalog-driven protocols. Rate is not a category — it lives on the drug record (Set B).
          One default per category; a drug falls back to it. {protocols.length} protocols · {components.length} components.
        </p>
      </div>

      {CATEGORIES.map(cat => (
        <div key={cat.key}>
          <div style={sectionHead}>
            {cat.icon}<span>{cat.label}</span>
            <span style={printsBadge}>prints to {cat.prints}</span>
            <span style={{ ...muted, fontSize: 12 }}>{(byCat[cat.key] || []).length} protocols</span>
            <span style={{ marginLeft: 'auto' }}>
              <AppButton size="small" variant="outlined" onClick={() => setEditor({ slot: cat.slot, protocol: null })}>+ New protocol</AppButton>
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
            {(byCat[cat.key] || []).map(p => (
              <div key={p.id} style={card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 700, fontSize: 13.5 }}>{p.name}</span>
                  <code style={codeChip}>{p.code}</code>
                  {p.is_default && <span style={defaultBadge}><Star size={11} /> default</span>}
                  {p.scope === 'pharmacy' && <span style={scopeBadge}>tenant override</span>}
                  <span style={{ ...muted, fontSize: 11 }}>v{p.version}</span>
                  {p.timing_minutes_prior != null && <span style={{ ...muted, fontSize: 11 }}>· {p.timing_minutes_prior} min prior</span>}
                  {!p.active && <span style={retiredBadge}>retired</span>}
                  {p.active && (
                    <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6 }}>
                      <AppButton size="small" variant="outlined" onClick={() => setEditor({ slot: cat.slot, protocol: p })}>Edit</AppButton>
                      <AppButton size="small" color="error" onClick={() => setRetiring(p)}>Retire</AppButton>
                    </span>
                  )}
                </div>
                {p.lines.length > 0 ? (
                  <table style={lineTable}>
                    <thead><tr>
                      <th style={th}>Component / line</th><th style={th}>Vol</th><th style={th}>Timing</th><th style={th}>Source</th><th style={th}>Sig</th>
                    </tr></thead>
                    <tbody>
                      {p.lines.map(l => (
                        <tr key={l.seq}>
                          <td style={{ ...td, fontStyle: l.is_free_text ? 'italic' : 'normal' }}>{l.item}</td>
                          <td style={td}>{l.volume_ml || '—'}</td>
                          <td style={td}>{l.timing || '—'}</td>
                          <td style={td}>{l.source ? <code style={srcChip}>{l.source}</code> : '—'}</td>
                          <td style={{ ...td, color: muted.color }}>{l.sig || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div style={{ ...muted, fontSize: 12, marginTop: 6 }}>Empty shell — admin defines contents and sigs.</div>
                )}
                {p.note && <div style={noteBox}>{p.note}</div>}
              </div>
            ))}
          </div>
        </div>
      ))}

      <div>
        <div style={sectionHead}><span>Component catalog</span><span style={{ ...muted, fontSize: 12 }}>{components.length} items</span></div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 8, marginTop: 8 }}>
          {CATEGORIES.map(cat => (
            <div key={cat.key} style={card}>
              <div style={{ fontWeight: 700, fontSize: 12.5, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>{cat.icon} {cat.label}</div>
              {(compByType[cat.key] || []).map(c => (
                <div key={c.id} style={{ fontSize: 12, padding: '3px 0', borderTop: '1px solid var(--color-border, #eee)' }}>
                  <div>{c.name}</div>
                  {c.default_sig && <div style={{ ...muted, fontSize: 11 }}>{c.default_sig}</div>}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {editor && (
        <ProtocolEditorDialog open slot={editor.slot} protocol={editor.protocol} components={components}
          drugReferenceId={null} onClose={() => setEditor(null)} onSaved={() => setEditor(null)} />
      )}
      <ProtocolRetireDialog protocol={retiring} onClose={() => setRetiring(null)} />
    </div>
  )
}

const muted = { color: 'var(--color-text-soft, #6b7280)' }
const cardTitle = { fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }
const sectionHead: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: 700, paddingBottom: 6, borderBottom: '2px solid var(--color-border, #e5e7eb)' }
const card: CSSProperties = { background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border, #e5e7eb)', borderRadius: 8, padding: '11px 13px' }
const lineTable: CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 8 }
const th: CSSProperties = { textAlign: 'left', padding: '4px 8px', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.4px', color: 'var(--color-text-soft, #6b7280)', borderBottom: '1px solid var(--color-border, #e5e7eb)' }
const td: CSSProperties = { padding: '4px 8px', borderBottom: '1px solid var(--color-border, #f1f5f9)', verticalAlign: 'top' }
const codeChip: CSSProperties = { fontSize: 11, background: 'var(--color-surface-soft, #f2f5f7)', borderRadius: 3, padding: '0 5px' }
const srcChip: CSSProperties = { fontSize: 10.5, background: 'var(--color-surface-soft, #eef2fd)', borderRadius: 3, padding: '0 4px', color: '#3b5bdb' }
const defaultBadge: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 800, color: '#0a7a1d', background: '#e9f7ec', border: '1px solid #b9e3c3', borderRadius: 9, padding: '1px 7px' }
const scopeBadge: CSSProperties = { fontSize: 10, fontWeight: 700, color: '#8a6d1f', background: '#fdf6e3', border: '1px solid #e8c97a', borderRadius: 9, padding: '1px 7px' }
const retiredBadge: CSSProperties = { fontSize: 10, fontWeight: 700, color: '#b23a48', background: '#fdf0f2', border: '1px solid #f0c2ca', borderRadius: 9, padding: '1px 7px' }
const printsBadge: CSSProperties = { fontSize: 10.5, color: 'var(--color-text-soft, #6b7280)', background: 'var(--color-surface-soft, #f7f9fa)', border: '1px solid var(--color-border, #e5e7eb)', borderRadius: 4, padding: '0 6px' }
const noteBox: CSSProperties = { marginTop: 8, fontSize: 12, color: '#8a6d1f', background: '#fdf6e3', borderLeft: '3px solid #e8c97a', borderRadius: '0 6px 6px 0', padding: '7px 10px' }
